import type { TDocumentDefinitions } from 'pdfmake/interfaces.js';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAttachmentFiles } from '../../attachments/AttachmentFiles.js';
import { loadReportFiles, type ReportFiles } from '../../attachments/report-files.js';
import { browserPdfOutput, pdfErrorKey, renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import type { RecordPdfT } from '../reports/record-pdf.js';
import type { ReportBrand } from '../voyage/source.js';
import {
  drydockCloseoutPdf,
  drydockJobListPdf,
  drydockReportFileName,
  type PdiReportAttachment,
  pdiInspectionReportPdf,
} from './report-pdf.js';
import type { DrydockReportOutput, DrydockScopeSource, PdiInspectionSource } from './source.js';
import type { DrydockCloseout, DrydockProject, DrydockScope, PdiInspectionWork } from './types.js';

export type ReportMode = 'save' | 'print';

/**
 * Pre-Docking raporlarının dışa aktarımı (ADR 0129 Ek Faz 5; tatbikat raporu emsali, ADR 0128 Faz 5
 * Ek): denetim raporu, Yard Job List ve kapanış raporu kaydedilir ya da yazdırılır. PDF istemcide,
 * internetsiz üretilir; marka okunamazsa yalnız şirket adı boş kalır. Ekler ortak yardımcıdan
 * (ADR 0130 §A): resim gömülü, diğer dosya web ofiste bağlantı, gemide "Open in Felixmate".
 * Hata metni döner (`null`: başarılı).
 */
export function useDrydockReportExport(output: DrydockReportOutput) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const tr: RecordPdfT = (key, options) => String(t(key, options ?? {}));
  const attachmentFiles = useAttachmentFiles();

  const run = async (
    name: string,
    mode: ReportMode,
    build: (brand: ReportBrand | null, printedAt: string) => Promise<TDocumentDefinitions>,
  ): Promise<string | null> => {
    setBusy(true);
    try {
      const brand = (await output.brand?.().catch(() => null)) ?? null;
      const definition = await build(brand, new Date().toISOString());
      const pdf = output.pdf ?? browserPdfOutput();
      await pdf[mode](name, () => renderPdf(definition, { print: mode === 'print' }));
      return null;
    } catch (error) {
      const key = pdfErrorKey(errorCode(error));
      return key === 'restHours.pdf.error' ? t('drydockReport.failed') : t(key);
    } finally {
      setBusy(false);
    }
  };

  /** Denetimin kanıtları: eki olan sonuçların ek listesi (okunamazsa yalnız sayısız rapor). */
  const evidence = async (
    work: PdiInspectionWork,
    source: PdiInspectionSource,
  ): Promise<{ attachments: PdiReportAttachment[]; files: ReportFiles }> => {
    const list = source.attachments;
    if (!list) return { attachments: [], files: {} };
    const attachments: PdiReportAttachment[] = [];
    for (const result of work.results.filter((r) => r.attachments > 0)) {
      const rows = await list(result.id).catch(() => []);
      for (const a of rows) {
        attachments.push({
          id: a.id,
          fileName: a.fileName,
          fileSize: a.size,
          mimeType: a.mimeType,
          sha256: a.sha256,
          voided: a.voided,
          checkpointCode: result.checkpointCode,
        });
      }
    }
    const url = source.attachmentUrl ?? attachmentFiles?.url;
    if (!url || attachments.length === 0) return { attachments, files: {} };
    const base = typeof window === 'undefined' ? null : window.location.href;
    return { attachments, files: await loadReportFiles(attachments, { url, base }) };
  };

  const inspection = (
    work: PdiInspectionWork,
    input: { source: PdiInspectionSource; scope?: DrydockScopeSource },
    mode: ReportMode,
  ) =>
    run(drydockReportFileName('pdi', work.inspectionNo), mode, async (brand, printedAt) => {
      const scope = input.scope ? await input.scope.load(work.projectId).catch(() => null) : null;
      const { attachments, files } = await evidence(work, input.source);
      return pdiInspectionReportPdf(tr, { work, scope, brand, printedAt, attachments, files });
    });

  const jobList = (scope: DrydockScope, mode: ReportMode) =>
    run(drydockReportFileName('jobs', scope.project.projectNo), mode, async (brand, printedAt) =>
      drydockJobListPdf(tr, { scope, brand, printedAt }),
    );

  const closeout = (
    project: DrydockProject,
    input: { scope: DrydockScope; closeout: DrydockCloseout | null },
    mode: ReportMode,
  ) =>
    run(drydockReportFileName('closeout', project.projectNo), mode, async (brand, printedAt) =>
      drydockCloseoutPdf(tr, { project, ...input, brand, printedAt }),
    );

  return { busy, inspection, jobList, closeout };
}
